
import React, { useMemo } from "react";
import { asArray, firstValid, getObjectValue, getTriggerAction, resolveExpression } from "../../utils";
import { getCellValue } from "../utils";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { computeThis } from "../../form/list/usecomputed";
import { Link } from 'react-router-dom';
import config from "../../../../config";

const adjustright = ["number", "date", "time", "datetime-local"]

export const colTyped = (col, data, pageData, options) => {
  let value = col.computed ? computeThis(col.expression, { local: data, global: pageData }) : getCellValue(data, col.path || col.name || "")

  switch (col.type) {
    case "select": return (options || []).find(item => item.value === (value || col.value))?.label || `(${value})`
    case "number": return col.decimal ? parseFloat(value) : parseInt(value)
    default: return value
  }
}

const asTextArray = (textArray, delimiter = ";") => typeof textArray === "string" ? textArray.split(delimiter) : (Array.isArray(textArray) ? textArray : undefined)

export const colGrow = size => (
  (size === 'xxs' && 0.2) ||
  (size === 'xs' && 0.5) ||
  (size === 'sm' && 0.8) ||
  (size === 'md' && 1.0) ||
  (size === 'lg' && 1.75) ||
  (size === 'xl' && 3.0) ||
  (size === 'xxl' && 5.0) || 1
)

const btnSizeObj = { sm: ["btn-sm"], md: [""], lg: ["btn-lg"] }
const btnGroupSizeObj = { sm: ["btn-group-sm"], md: [""], lg: ["btn-group-lg"] }
const btnSizeMap = { "btn-sm": 'sm', "btn-md": 'md', "": 'md', "btn-lg": "lg" }

const getButtonClassNames = (colStyle, cellStyle, cellSize, groupSize) => {
	const className = [
		//"btn", groupSize ? [] : ["p-1", "mr-1", "mt-1"], "btn-outline-default"
		"btn", "btn-outline-default"
	].flat();
	
	colStyle = (asTextArray(colStyle, " ") || []).map(txt => asTextArray(txt)).flat()
	cellStyle = (asTextArray(cellStyle, " ") || []).map(txt => asTextArray(txt)).flat()
	
	const buttonClassStyles = [...colStyle, ...cellStyle]
	const configSize = buttonClassStyles.filter( s => ["btn-sm", "btn-md", "btn-lg"].includes(s) ).at(-1);
	
	const buttonClasses = [
		...( buttonClassStyles.includes("self-style") ? ["btn"] : className ),
		...buttonClassStyles.filter( s => !(["btn-sm", "btn-md", "btn-lg"].includes(s)) ),
		...( firstValid(btnSizeObj[groupSize || cellSize], btnSizeObj[btnSizeMap[configSize]] , ["btn-sm"]) )
	]

	return buttonClasses
}

const getButtonActualSize = (col, cell) => btnSizeMap[getButtonClassNames(col.style, cell.style, cell.size).at(-1)] ||"sm"


export const buttonCell = (meta, col, pageData, formId) => (row, index, defaultbuttons) => {
  const value = col.computed ? computeThis(col.expression, { local: row, global: pageData }) : getObjectValue(row, col.path || col.name || "")
  const cells = asArray(firstValid(value, defaultbuttons, []))

  const clickHandler = cell => cell.action || col.action ? getTriggerAction({ actionNames: cell.action || col.action, meta, withData: col.data, pageData })({ ...row, _index: index }) : null
  
  const buttonmap = (cell, i, groupSize) => {
		const buttonClasses = getButtonClassNames(col.style, cell.style, cell.size, groupSize)

		const isLink = !(cell.action || col.action) && (cell.page || cell.file || col.page || col.file)
		const cursor = !(cell.action || col.action) && !isLink ? "default" : (col.readonly || cell.disabled) ? "not-allowed" : "pointer"

		const linkOrButtonProps = {
			disabled: col.readonly || cell.disabled,
			type: "button",
			title: cell.placeholder || col.placeholder,
			className: buttonClasses.join(" "),
			style: { cursor },
			key: `cell-btn-${cell.group || 'def'}-${i}`
		}

		const children = [
			cell.icon && <FontAwesomeIcon key={`cell-icon-${i}`} className="mx-1" icon={cell.icon?.split(" ")} />,
			cell.label && <span key={`cell-label-${i}`} className="mx-1">{cell.label}</span>
		]

		const isFile = isLink && !( cell.page || col.page ) 

		if (isFile) {
			linkOrButtonProps.href = encodeURI(`${config.apiUrl}/file/${cell.file || col.file}`)
			return <a {...linkOrButtonProps}>{children}</a>
		}
		else if (isLink) {
			linkOrButtonProps.to = encodeURI(`/${cell.page || col.page}`)
			return <Link {...linkOrButtonProps}>{children}</Link>
		}
		else {
			linkOrButtonProps.onClick = clickHandler(cell)
			return <button {...linkOrButtonProps} form={formId}>{children}</button>
		}
	}

	const validButtons = cells.filter( e => !!e ) //only buttons that exists
	const groupedButtons = validButtons
		.map(({ group = 'default' }, idx) => ({ group, idx }) )
		.reduce ( (groups, { group, idx }) => {
			let last = groups.pop();
			
			if (last?.group === group && group !== "default") {
				last.idx = [...(last.idx), idx]
			} else {
				if (last) groups.push(last);
				last = { group, idx: [idx] }
			}
			groups.push(last);

			return [...groups]
		}, [])

	return (
		<>
			{
				groupedButtons.map( ({ group, idx = [] }, gidx) => {
					const grouped = group !== 'default'
					const baseClassName = [...(grouped ? ["btn-group"] : ['d-inline']), ...(gidx ? ['ml-1'] : [])]
					const groupSize = grouped ? btnGroupSizeObj[getButtonActualSize(col, validButtons[idx[0]])] : []
					
					const { dropdown, style, size, disabled } = validButtons[idx[0]]
					const dropdownClassNames = dropdown ? ["dropdown-toggle", ...getButtonClassNames(col.style, style, size)].join(' ') : null

					const children = idx
						.map( i => validButtons[i])
						.map((cell, i) => {
							const buttonCell = typeof cell === "string" ? { label: cell } : cell instanceof Object ? { ...cell } : null
							
							if (!buttonCell) return null
							if (!buttonCell.label && !buttonCell.icon && dropdown) return <div className="dropdown-divider" />;
							
							const isHeader = !(buttonCell.action || col.action) && !(buttonCell.page || col.page) && !(buttonCell.file || col.file)
							if (isHeader && dropdown) return <h6 className="dropdown-header" >{buttonCell.label}</h6>;
							
							if (dropdown) buttonCell.style = `${buttonCell.style || ""} dropdown-item`

							return buttonmap(buttonCell, i, grouped ? "md" : undefined)
						})

					return dropdown 
					? (
						<div 
							className={["dropdown", ...(gidx ? ['ml-1'] : [])].join(' ')}
						>
							<button type="button" disabled={!!(col.readonly || disabled)} className={dropdownClassNames} data-toggle="dropdown">{dropdown}</button>
							<div className="dropdown-menu">
								{ children }
							</div>
						</div>
					)
					: (
						<div 
							key={`${ formId ? 'btn' : 'cell' }-${group}-${gidx}`}
							className={ [baseClassName, groupSize].flat().join(' ') }
						>
							{ children }
						</div>
					)

				})
			}
		</>
	)

}



const colFormatter = (col) =>
  (col.formatter && !col.computed) ? data => {
    const value = getCellValue(data, col.path || col.name || "")
    if (col.type === "number")
      return isNaN(value) ? undefined : col.formatter(data.currency, value).format(value)
    else
      return col.formatter(undefined, value).format(value);
  }
    : undefined

const columnDefaultValue = (global, local, value) => value && resolveExpression(value, { global, local })

export function useColumnsMemo({ structOptions = [], struct = [], meta, pageData }) {
  const columns = useMemo(() => {
		return struct.map((col, idx) => {
			
      return {
        name: col.title || col.label || col.name,
        selector: data => colTyped(col, data, pageData, structOptions[idx]),
        format: colFormatter(col),
        sortable: col.type !== "auto",
        right: adjustright.some(coltype => col.type === coltype) ? true : false,
        center: false,
        omit: col.hideonboard,
        minWidth: `${100 * colGrow(col.size)}px`,
        maxWidth: `${300 * colGrow(col.size)}px`,
        grow: colGrow(col.size),
        cell: col.type === "button" ? (row, idx) => buttonCell(meta, col, pageData)(row, idx, columnDefaultValue(pageData, row, col.value)) : null
      }
    })
  }, [struct, meta, structOptions, pageData])


  return columns;
}


